/* global BigInt */

import type * as Types from "../types"
import { KeyValue, NamedValue } from '../types'

import { alwaysTruthyPredicate, isEqual } from './functions'

/*****************************
 * Private
 * - Consts
 ****************************/

const HAS_TO_STRING_TAG =
	typeof Symbol === "function" && typeof Symbol.toStringTag === "symbol"

/*****************************
 * Private
 * - Utility Functions
 ****************************/

function tryToString(
	value: unknown,
	Clazz: { prototype: { toString: () => string } }
): boolean {
	try {
		Clazz.prototype.toString.call(value)
		return true
	} catch (e) {
		return false
	}
}

function tryValueOf(
	value: Types.AnyRecord,
	Clazz: { prototype: { valueOf: () => unknown } }
): boolean {
	try {
		Clazz.prototype.valueOf.call(value)
		return true
	} catch (e) {
		return false
	}
}

function callObjectToString(value: unknown): string {
	return Object.prototype.toString.call(value)
}

/*****************************
 * Public
 * - Simple Type Guards
 ****************************/

/**
 * Tests if a value is a string. This is an alias for 'typeof value === "string"'.
 *
 * @param value - value to test
 */
export function isString(value: unknown): value is string {
	return typeof value === "string"
}

/**
 * Tests if a value is a number. This is an alias for 'typeof value === "number"', but also verifies
 * that the value is non 'NaN'.
 *
 * @param value - value to test
 */
export function isNumber(value: unknown): value is number {
	return typeof value === "number" && !isNaN(value)
}

/**
 * Tests if a value is a boolean. This is an alias for 'typeof value === "boolean"'
 *
 * @param value - value to test
 */
export function isBoolean(value: unknown): value is boolean {
	return typeof value === "boolean"
}

/**
 * Tests if a value is a symbol. This has additional checks to verify Symbol support
 * in browsers.
 *
 * @param value - value to test
 */
export function isSymbol(value: unknown): value is symbol {
	if (typeof Symbol !== "function") return false
	if (typeof value === "symbol") return true
	if (callObjectToString(value) === "[object Symbol]") return true
	if (tryToString(value, Symbol)) return true
	return false
}

/**
 * Tests if a value is null. This is an alias for 'value === null'
 *
 * @param value - value to test
 */
export function isNull(value: unknown): value is null {
	return value === null
}

/**
 * Tests if a value is null. This is an alias for 'value === undefined || typeof value === "undefined"'
 *
 * @param value - value to test
 */
export function isUndefined(value: unknown): value is undefined {
	return value === undefined || typeof value === "undefined"
}
/**
 * Tests if a value is null. This is an alias for 'value === undefined || typeof value === "undefined"'
 *
 * @param value - value to test
 */
export function isArray<T = unknown>(value: unknown): value is T[] {
	return Array.isArray(value)
}

/**
 * Tests if a value is an object. This will also check that the object is not an array, or null.
 *
 * @param value - value to test
 */
export function isObject<O extends Record<Types.Key, any>>(value: O): value is O
export function isObject(value: unknown): value is Types.AnyRecord
export function isObject<O extends Record<Types.Key, any> | unknown = unknown>(
	value: O
): value is O {
	return typeof value === "object" && !isNull(value) && !isArray(value)
}

/**
 * Tests if a value is a function.
 *
 * @param value - value to test
 */
export function isFunction(value: unknown): value is (...args: any[]) => any {
	return isDefined(value) && {}.toString.call(value) === "[object Function]"
}

/*****************************
 * Public
 * - Literal Type Guards
 ****************************/

/**
 * Tests if a value is an empty string.
 *
 * @param value - value to test
 */
export function isEmptyString(value: unknown): value is Types.EmptyString {
	return isString(value) && value === ""
}
/**
 * Tests if a value is truthy. Will return false if value is null, undefined or false.
 *
 * @param value - value to test
 */
export function isPresent<T = unknown>(value: Types.MaybeOptional<T>): value is T {
	return isValued(value as any) && value !== false
}

/*****************************
 * Public
 * - Negation Type Guards
 ****************************/

/**
 * Tests if a value is defined. Will return false if value is undefined.
 *
 * @param value - value to test
 */
export function isDefined<T = unknown>(value: Types.Optional<T>): value is T {
	return value !== undefined
}

/**
 * Tests if a value is not null. Will return false if value is null.
 *
 * @param value - value to test
 */
export function isNonNull<T = unknown>(
	value: Types.Nullable<Exclude<T, null>>
): value is Exclude<T, null> {
	return value !== null
}

/**
 * Tests if a value is valued. Will return false if value is undefined or null.
 *
 * @param value - value to test
 */
export function isValued<T = unknown>(value: Types.Nullable<T>): value is T {
	return value !== undefined && value !== null
}

/*****************************
 * Public
 * - Entry Type Guards
 ****************************/

/**
 * Tests if a value is an {@link Types.Entry}. Will return false if value is not an array, 
 * or not of length 2.
 *
 * @param value - value to test
 */
export function isEntry<K = unknown, V = unknown>(
	value: unknown
): value is Types.Entry<K, V> {
	return isArray(value)
		&& value.length === 2
}

/**
 * Tests if an {@link Types.Entry} contains valued values. Will return false if either 
 * the key or value is undefined.
 *
 * @param entry - entry to test
 */
export function isEntryValued<V = unknown>(
	entry: Types.Entry<unknown, V>
): entry is Types.Entry<NonNullable<string>, NonNullable<V>> {
	return entry.every(isValued)
}

function isEntryOfTypeFactory<K = unknown, V = unknown>(
	predicate: Types.EntryTypeGuard<K, V>
){
	return function isEntryOfType(entry: unknown): entry is [K, V] {
		return isEntry<K, V>(entry) && predicate(entry)
	}
}

export function doesEntryHaveKeyFactory<K = unknown>(
	keyToFind: K,
	isKeyEqual: Types.EqualityFn<unknown> = isEqual
): Types.TypeGuard<[K, unknown]> {
	return (value: unknown): value is [K, unknown] => 
		isEntryOfTypeFactory(
			(entry): entry is [K, unknown] => 
				isKeyEqual(entry[0], keyToFind)
		)(value)
}

export function doesEntryHaveValueFactory<V = unknown, K = unknown>(
	valueToFind: V,
	isValueEqual: Types.EqualityFn<unknown> = isEqual
): Types.TypeGuard<[unknown, V]> {
	return (value: unknown): value is [K, V] => 
		isEntryOfTypeFactory(
			(entry): entry is [unknown, V] => 
				isValueEqual(entry[1], valueToFind)
		)(value)
}

/*****************************
 * Public
 * - Object Type Guards
 ****************************/

// eslint-disable-next-line @typescript-eslint/ban-types
export function isNumberObject(value: unknown): value is Number {
	if (isNumber(value)) return true
	if (!isObject(value)) return false
	if (HAS_TO_STRING_TAG && Symbol.toStringTag in value) {
		return tryToString(value, Number)
	} else {
		return callObjectToString(value) === "[object Number]"
	}
}
// eslint-disable-next-line @typescript-eslint/ban-types
export function isBooleanObject(value: unknown): value is Boolean {
	if (isBoolean(value)) return true
	if (!isObject(value)) return false
	if (HAS_TO_STRING_TAG && Symbol.toStringTag in value) {
		return tryToString(value, Boolean)
	} else {
		return callObjectToString(value) === "[object Boolean]"
	}
}

export function isDate(value: unknown): value is Date {
	return value instanceof Date
}
export function isBigInt(value: unknown): value is BigInt {
	if (typeof BigInt !== "function") return false
	if (typeof value === "bigint") return true
	if (!isObject(value)) return false
	if (tryValueOf(value, BigInt)) return true
	return false
}

export function isKeyValue<K, V>(value: unknown): value is KeyValue<K, V> {
	const asType = value as KeyValue<K, V>
	return isObject(asType)
		&& isDefined(asType.key)
		&& hasKey(asType, 'value')
}

export function isNamedValue<V>(value: unknown): value is NamedValue<V> {
	const asType = value as NamedValue<V>
	return isObject(asType)
		&& isString(asType.name)
		&& hasKey(asType, 'value')
}

/*****************************
 * Public
 * - Validator Type Guards
 ****************************/

export function isOneOf<T1, T2, T3, T4>(
	...validators: [Types.TypeGuard<T1>, Types.TypeGuard<T2>, Types.TypeGuard<T3>, Types.TypeGuard<T4>]
): Types.TypeGuard<T1 | T2 | T3 | T4>
export function isOneOf<T1, T2, T3>(
	...validators: [Types.TypeGuard<T1>, Types.TypeGuard<T2>, Types.TypeGuard<T3>]
): Types.TypeGuard<T1 | T2 | T3>
export function isOneOf<T1, T2>(
	...validators: [Types.TypeGuard<T1>, Types.TypeGuard<T2>]
): Types.TypeGuard<T1 | T2>
export function isOneOf<T1>(
	...validators: Types.TypeGuard<T1>[]
): Types.TypeGuard<T1>
export function isOneOf<T1, T2, T3, T4>(
	...validators: Types.TypeGuard<T1 | T2 | T3 | T4>[]
): Types.TypeGuard<T1 | T2 | T3 | T4> {
	return (value: unknown): value is T1 | T2 | T3 | T4 =>
		validators.some((validator) => validator(value))
}

export function isPairOf<T extends any>(
	validator: (value: unknown) => value is T
) {
	return (value: unknown): value is Types.Pair<T> => {
		return isArray(value) && value.every(validator) && value.length === 2
	}
}

export function isLiteralOf<Literal extends string | number>(
	literal: Literal
): Types.TypeGuard<Literal> {
	return (value: unknown): value is Literal => value === literal
}

export function isEnumOf<Enum extends Types.StringOrNumber>(
	enumValues: Types.ReadonlyOrMutable<Enum[]> = []
): Types.TypeGuard<Enum> {
	if(enumValues.length <= 0) return alwaysTruthyPredicate as Types.TypeGuard<Enum>
	return (value: unknown): value is Enum => enumValues.includes(value as Enum)
}

export function isKeyOfRecordOf<Key extends string>(
	record: Types.ReadonlyOrMutable<Types.DictionaryRecord<Key, any>> = {}
): Types.TypeGuard<Key> {
	const keys = Object.keys(record).filter(isDefined) as Key[]

	const isKeyOfRecord = isEnumOf<Key>(keys)
	return (value: unknown): value is Key => isKeyOfRecord(value)
}

export function isValueOfRecordOf<Value extends Types.StringOrNumber>(
	record: Types.ReadonlyOrMutable<Types.Dictionary<Value>> = {}
): Types.TypeGuard<Value> {
	const values = Object.values(record).filter(isDefined)

	const isValueOfRecord = isEnumOf<Value>(values)
	return (value: unknown): value is Value => isValueOfRecord(value)
}

export function isDictionaryOf<T>(
	validate: (value: unknown) => value is T
): Types.TypeGuard<Types.Dictionary<T>> {
	return (value: unknown): value is Types.Dictionary<T> => {
		const asType = value as Types.Dictionary<T>
		return isObject(asType) && Object.values(asType).every(validate)
	}
}

export function isArrayOf<T extends any>(
	validator: Types.TypeGuard<T>
): Types.TypeGuard<T[]> {
	return (list: unknown): list is T[] =>
		isArray(list) && list.every(validator)
}

export function isRecordOf<K extends string, T>(
	keys: K[],
	validate: Types.TypeGuard<T>
): Types.TypeGuard<Record<K, T>> {
	return (value: unknown): value is Record<K, T> => {
		const asType = value as Record<K, T>
		return (
			isObject(asType) &&
			keys.every((key) => isDefined(asType[key]) && validate(asType[key]))
		)
	}
}

export function isSingleOrArrayOf<V>(
	validator: Types.TypeGuard<V>
){
	return function isSingleOrArray(value: unknown): value is Types.SingleOrArray<V> {
		return validator(value) || isArrayOf(validator)(value)
	} 
} 

/*****************************
 * Public
 * - Property Type Guards
 ****************************/

export function hasLength(value: unknown): value is Types.Lengthed {
	return (
		isValued(value) &&
		(isObject(value) || isArray(value)) &&
		"length" in value
	)
}

export function hasKey<K extends Types.Key, V>(
	value: Types.AnyRecord,
	key: K
): value is { [Key in K]: V }
export function hasKey<K extends Types.Key>(
	value: unknown,
	key: K
): value is { [Key in K]: unknown }
export function hasKey<K extends string, V>(
	value: Types.AnyRecord | unknown,
	key: K
): value is { [Key in K]: V | unknown } {
	const asType = value as { [L in K]: V }
	return isObject(asType) && Object.prototype.hasOwnProperty.apply(asType, [key])
}

export function isStringOfLength<S extends string, L extends number>(
	value: unknown,
	length: L
): value is Types.StringOfLength<S, L> {
	return isString(value) && value.length === length
}

export function isValidEmail(value: unknown): value is `${string}@${string}.${string}`{
	const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return isString(value) && 
	emailPattern.test(String(value).toLowerCase());
}

/*****************************
 * Public
 * - Compound Type Guards
 ****************************/

/**
 * Tests if a value is a string. This is an alias for 'typeof value === "string"'.
 *
 * @param value - value to test
 */
export function isPrimitiveValueOrDate(value: unknown): value is Types.PrimitiveValueOrDate {
	return isOneOf(
		isString,
		isNumber,
		isBoolean,
		isDate
	)(value)
}

/**
 * Tests if value is either a string or a number. This is an alias for 'isString(value) || isNumber(value)'.
 */
export const isStringOrNumber = isOneOf(isString, isNumber)

/**
 * Tests if value is either an optional string. This is an alias for 'isString(value) || isUndefined(value)'.
 */
export const isOptionalString = isOneOf(isString, isUndefined)

/**
 * Tests if value is either an optional number. This is an alias for 'isNumber(value) || isUndefined(value)'.
 */
export const isOptionalNumber = isOneOf(isString, isUndefined)

/**
 * Tests if value is either an optional boolean. This is an alias for 'isBoolean(value) || isUndefined(value)'.
 */
  export const isOptionalBoolean = isOneOf(isBoolean, isUndefined)

/**
 * Tests if value is either an optional Date. This is an alias for 'isDate(value) || isUndefined(value)'.
 */
export const isOptionalDate = isOneOf(isDate, isUndefined)